import { useSnackbar } from 'components/snackbar';
import {
  Grid,
  TableHead,
  TableContainer,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  CardContent,
  Card,
  Box,
  Button,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { getCandidateInfo } from 'api/admin';
import CloseIcon from '@mui/icons-material/Close';
import { setApplicationStatus } from 'api/admin';
import { setFeedbackDetails } from 'api/admin';
import { useAuth } from 'hooks/useAuthContext';
import { useForm, Controller } from 'react-hook-form';
import { deleteFeedback } from 'api/admin';
import { getApplicationFeedback } from 'api/feedback';

const dayjs = require('dayjs');

/**
 * Applicant Profile component
 * 
 * This component displays the profile of an applicant. It shows the relevant details of the applicant like name, phone number, email, and job specific and general parameters ratings.
 * It also displays the application status and the scores of the pre-assessment, questionnaire, career progression, communication, and behavioral skills of the applicant.
 * Additionally, it allows the user to add a feedback for the candidate and related to application.
 * It also displays the feedback list of the candidate and application.
 * The user can delete any feedback added by them.
 * Finally, it displays the profile details of the candidate like work history and education history.
 * 
 * @prop {Object} selectedApplicant - the selected applicant object
 * @prop {Function} onGoBack - the function to be called when the user clicks on the go back button
 */
const ApplicantProfile = ({ selectedApplicant, onGoBack, fetchApplicants }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { _id } = selectedApplicant;
  const [loading, setLoading] = useState(true);
  const [applicants, setapplicants] = useState({});
  const [ratings, setRatings] = useState({})
  const [status, setStatus] = useState(selectedApplicant?.status || '');
  const [scores, setScores] = useState({
    pre_assessment_score: "NA",
    questionnaire_score: "NA",
    career_progression_score: "NA",
    communication_score: "NA",
    finance_understanding_score: "NA",
    behavioral_score: "NA",
    technical_subject_score: "NA",
    problem_solving_score: "NA",
    job_stability_score: "NA"
  });
  // feedback for candidate
  const [candidateFeedback, setCandidateFeedback] = useState('');
  const [visibleToCompany, setForCompany] = useState(true);
  const [visibleToCandidate, setForCandidate] = useState(true);
  // feeback related to application
  const [applicationFeedback, setapplicationFeedback] = useState('');
  const [visibleToCompanyApplication, setForCompanyApplication] = useState(true);
  const [visibleToCandidateApplication, setForCandidateApplication] = useState(true);
  // feedbcak list
  const [feedbacks, setFeedbacks] = useState(null);
  const { user } = useAuth();
  const userId = user?.id;

  const candidateFeedbackData = {
    feedback_by: userId,
    for: selectedApplicant.user_id,
    job_id: selectedApplicant?.job_id,
    company_id: selectedApplicant?.company_id,
    application_status: status,
    feedback: candidateFeedback,
    visible_to_candidate: visibleToCandidate,
    visible_to_company: visibleToCompany,
    status: 'active',
  };
  const applicationFeedbackData = {
    feedback_by: userId,
    for: selectedApplicant.user_id,
    job_id: selectedApplicant?.job_id,
    company_id: selectedApplicant?.company_id,
    application: selectedApplicant._id,
    application_status: status,
    feedback: applicationFeedback,
    visible_to_candidate: visibleToCandidateApplication,
    visible_to_company: visibleToCompanyApplication,
    status: 'active',
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [selectedApplicant]);

  /**
   * Fetches data for the selected applicant and the job.
   * The data fetched includes the application, ratings, and feedbacks
   * for the selected applicant.
   * The data is fetched from the server and updated in the state.
   * If the fetch is successful, it updates the applicants, ratings, and feedbacks
   * states with the received data. If the fetch is unsuccessful, it logs
   * an error to the console.
   * @returns {undefined}
   */
  const fetchData = async () => {
    try {
      const response = await getCandidateInfo({ _id, userId });
      const feedbackResponse = await getApplicationFeedback({ jobId: selectedApplicant?.job_id?._id, userId: selectedApplicant?.user_id?._id, })
      if (response?.success && response.data && feedbackResponse.success) {
        setapplicants(response?.data?.applicant);
        setRatings(response?.data);
        setFeedbacks(feedbackResponse?.data);
        setStatus(response?.data?.applicant?.status);
        const assessmentScore = response?.data?.applicant?.assessment_score;
        setScores(assessmentScore ? assessmentScore : {
          pre_assessment_score: "NA",
          questionnaire_score: "NA",
          career_progression_score: "NA",
          communication_score: "NA",
          finance_understanding_score: "NA",
          behavioral_score: "NA",
          technical_subject_score: "NA",
          problem_solving_score: "NA",
          job_stability_score: "NA",
        });
      } else {
        console.error('Failed to fetch job info:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching job info:', error);
    }
    setLoading(false);
  };

  /**
   * Handles the go back action.
   * If the `onGoBack` callback function is provided, it will be called to
   * handle the go back action. Otherwise, it will do nothing.
   * @returns {undefined}
   */
  const handleGoBack = () => {
    if (typeof onGoBack === 'function') {
      onGoBack();
    }
  };
  /**
   * Handles the score input change event.
   * It updates the scores state with the new value of the selected
   * score input and calls the handleApplicationScore function with
   * the new score and label.
   * @param {Event} event The change event.
   * @param {string} label The label of the input that was changed.
   * @returns {undefined}
   */
  const handleScore = (event, label) => {
    const selectedValue = event.target.value;
    setScores(prevScores => ({
      ...prevScores,
      [label]: selectedValue
    }));
    handleApplicationScore(selectedValue, label);
  };
  /**
   * Handles the application score update action.
   * Makes an API call to update the application status with the new score
   * and label. If the API call is successful, it displays a success message.
   * If the API call fails, it displays an error message.
   * @param {number} score The new score of the application.
   * @param {string} label The label of the score input that was changed.
   * @returns {Promise<void>}
   */
  const handleApplicationScore = async (score, label) => {
    try {
      const data = { [label]: score };
      const response = await setApplicationStatus({ _id, data });
      if (response?.success === true && response.data) {
        enqueueSnackbar('Score update successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to update score', { variant: 'error' });
        console.error('Failed to update score:', response.message);
      }
    } catch (error) {
      console.error('Error updating score:', error);
      enqueueSnackbar('An error occurred while updating score', { variant: 'error' });
    }
  };
  /**
   * Handles the application status update action.
   * It calls the handleApplicationStatus function with the new status
   * value. If the API call is successful, it displays a success message.
   * If the API call fails, it displays an error message.
   * @param {Event} event The change event.
   * @returns {undefined}
   */
  const handleStatus = (event) => {
    setStatus(event.target.value);
    handleApplicationStatus(event.target.value);
  };
  /**
   * Handles the application status update action.
   * Makes an API call to update the application status with the new status
   * value. If the API call is successful, it displays a success message.
   * If the API call fails, it displays an error message.
   * @param {string} status The new status of the application.
   * @returns {Promise<void>}
   */
  const handleApplicationStatus = async (status) => {
    try {
      const response = await setApplicationStatus({ _id, status });
      if (response?.success === true && response.data) {
        fetchApplicants();
        enqueueSnackbar('Status update successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to update status', { variant: 'error' });
        console.error('Failed to update status:', response.message);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      enqueueSnackbar('An error occurred while updating status', { variant: 'error' });
    }
  };

  // candidate feedback
  const handleCandidateFeedback = (event) => {
    setCandidateFeedback(event.target.value);
  };
  /**
   * Toggles the state of the `forCandidate` variable.
   * When `forCandidate` is true, the component displays a form for the
   * recruiter to write feedback for the candidate.
   * When `forCandidate` is false, the component displays a form for the
   * recruiter to write feedback for the company.
   * @returns {undefined}
   */
  const handleCandidate = () => {
    setForCandidate(prevState => !prevState);
  };
  /**
   * Toggles the state of the `forCompany` variable.
   * When `forCompany` is true, the component displays a form for the
   * recruiter to write feedback for the company.
   * When `forCompany` is false, the component displays a form for the
   * recruiter to write feedback for the candidate.
   * @returns {undefined}
   */
  const handleCompany = () => {
    setForCompany(prevState => !prevState);
  };
  //feedback for candidate
  const handleSubmitCandidateFeedback = async () => {
    try {
      const response = await setFeedbackDetails(candidateFeedbackData);
      if (response?.success === true && response.data) {
        enqueueSnackbar('Feedback sent successfully', { variant: 'success' });
        setCandidateFeedback('');

      } else {
        enqueueSnackbar('Failed to send feedback', { variant: 'error' });
        console.error('Failed to send feedback:', response.message);
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      enqueueSnackbar('An error occurred while sending feedback', { variant: 'error' });
    }
  };

  // application feedback
  const handleApplicationFeedback = (event) => {
    setapplicationFeedback(event.target.value);
  };
  /**
   * Toggles the state of the `forCandidateApplication` variable.
   * When `forCandidateApplication` is true, the component displays a form for the
   * recruiter to write feedback for the candidate in relation to the application.
   * When `forCandidateApplication` is false, the component does not display a form
   * for the recruiter to write feedback for the candidate in relation to the application.
   * @returns {undefined}
   */
  const handleCandidateApplication = () => {
    setForCandidateApplication(prevState => !prevState);
  };
  /**
   * Toggles the state of the `forCompanyApplication` variable.
   * When `forCompanyApplication` is true, the component displays a form for the
   * recruiter to write feedback for the company in relation to the application.
   * When `forCompanyApplication` is false, the component does not display a form
   * for the recruiter to write feedback for the company in relation to the application.
   * @returns {undefined}
   */
  const handleCompanyApplication = () => {
    setForCompanyApplication(prevState => !prevState);
  };
  //feedback related to application
  const handleSubmitApplicationFeedback = async () => {
    try {
      const response = await setFeedbackDetails(applicationFeedbackData); // Use formDataWithIds here
      if (response?.success === true && response.data) {
        setFeedbacks(prevFeedbackList => [response.data, ...prevFeedbackList]);
        fetchData();
        enqueueSnackbar('Feedback sent successfully', { variant: 'success' });
        setapplicationFeedback('');
      } else {
        enqueueSnackbar('Failed to send feedback', { variant: 'error' });
        console.error('Failed to send feedback:', response.message);
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      enqueueSnackbar('An error occurred while sending feedback', { variant: 'error' });
    }
  };

  // delete feedback
  const handleDeleteFeedback = async (feedbackId) => {
    try {
      const response = await deleteFeedback({ feedbackId });
      if (response?.success === true && response.data) {
        setFeedbacks(prevFeedbackList => prevFeedbackList.filter(feedback => feedback._id !== feedbackId));
        enqueueSnackbar('Feedback deleted successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to delete feedback', { variant: 'error' });
        console.error('Failed to delete feedback:', response.message);
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      enqueueSnackbar('An error occurred while deleting feedback', { variant: 'error' });
    }
  };

  /**
   * Opens the resume of the applicant in a new tab.
   * @returns {undefined}
   */
  const handleNavigation = () => {
    window.open(applicants.resume, '_blank');
  }

  const getRatingLabel = (value) => {
    switch (value) {
      case 4:
        return "Excellent";
      case 3:
        return "Above Average";
      case 2:
        return "Average";
      case 1:
        return "Below Average";
      default:
        return "NA";
    }
  };

  const scoreLabels = {
    1: "Poor",
    2: "Average",
    3: "Good",
    NA: "NA",
  };

  if (!selectedApplicant) {
    return (
      <Box>
        <Typography variant="h6">No applicant selected</Typography>
      </Box>
    );
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: '-25px' }}>
      <Container>
        <Box sx={{ margin: '2rem 0', marginBottom: '1rem' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>Applicant Profile</Typography>
            <IconButton onClick={handleGoBack}><CloseIcon /></IconButton>
          </Stack>
          <Paper>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography>Name: {applicants?.candidate_profile?.full_name || 'N/A'}</Typography>
              <Typography>Phone Number: {applicants?.candidate_profile?.phone_number || 'N/A'}</Typography>
              <Typography>Email: {applicants?.candidate_profile?.email || 'N/A'}</Typography>
              <Typography gutterBottom style={{ textAlign: 'right' }}>Status: {status}</Typography>
            </Stack>
            {/* education */}
            {applicants?.requirements_matched?.education?.length > 0 &&
              <Box>
                <Typography color="h6" sx={{ fontSize: '1rem', fontWeight: 'normal', marginBottom: '1rem', marginTop: '1rem' }} >
                  <strong>Education History</strong>
                </Typography>
                <Paper elevation={3} sx={{ marginBottom: '1rem' }}>
                  <div style={{ height: 'auto', width: '100%' }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Institute Name</TableCell>
                            <TableCell>Degree</TableCell>
                            <TableCell>Period of Start From</TableCell>
                            <TableCell>Period of End To</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {applicants?.requirements_matched?.education.length === 0 ? (
                            <TableRow><TableCell colSpan={8}>No work history added yet.</TableCell></TableRow>
                          ) : (applicants?.requirements_matched?.education
                            .sort((a, b) => new Date(b.education_from) - new Date(a.education_from))
                            .map((item, index) => (
                              <TableRow key={item._id || index}>
                                <TableCell>{item?.education_institute_name || 'N/A'}</TableCell>
                                <TableCell>{item?.degree_received || 'N/A'}</TableCell>
                                <TableCell>{dayjs(item?.education_from).format('MMM, YYYY')}</TableCell>
                                <TableCell>{dayjs(item?.education_to).format('MMM, YYYY')}</TableCell>
                              </TableRow>
                            )))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </Paper>
              </Box>}
            {/* work history */}
            {applicants?.requirements_matched?.work_history?.length > 0 &&
              <Box>
                <Typography color="h6" sx={{ fontSize: '1rem', fontWeight: 'normal', marginBottom: '1rem', marginTop: '1rem' }}>
                  <strong>Work History</strong>
                </Typography>
                <Paper elevation={3} sx={{ marginBottom: '1rem' }}>
                  <div style={{ height: 'auto', width: '100%' }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Company Name</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Period of Work From</TableCell>
                            <TableCell>Period of Work To</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {applicants?.requirements_matched?.work_history.length === 0 ? (
                            <TableRow><TableCell colSpan={8}>No work history added yet.</TableCell></TableRow>
                          ) : (applicants?.requirements_matched?.work_history
                            .sort((a, b) => new Date(b.work_history_from) - new Date(a.work_history_from))
                            .map((item, index) => (
                              <TableRow key={item._id || index}>
                                <TableCell>{item?.organization_name || 'N/A'}</TableCell>
                                <TableCell>{item?.work_history_location || 'N/A'}</TableCell>
                                <TableCell>{dayjs(item?.work_history_from).format('MMM, YYYY')}</TableCell>
                                <TableCell>{item?.work_history_to ? dayjs(item?.work_history_to).format('MMM, YYYY') : 'Present'}</TableCell>
                              </TableRow>
                            )))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </Paper>
              </Box>
            }
            <Box sx={{ marginBottom: '1rem' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom style={{ textAlign: 'left' }}>
                    Candidate Relevance Score
                  </Typography>
                </Box>
                <Box>
                  <Typography gutterBottom style={{ textAlign: 'right' }}>
                    Applicant Rating: {applicants?.tj_score >= 3 ? 'Highly Suitable' : applicants?.tj_score > 1 ? 'Moderately Suitable' : 'Not Suitable'}
                  </Typography>
                </Box>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <h3>JD Specific</h3>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>Experience Level</TableCell>
                          <TableCell>{getRatingLabel(applicants?.candidate_score?.experienceMatchRating)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Skills Match</TableCell>
                          <TableCell>{getRatingLabel(applicants?.candidate_score?.skillMatchRating)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Industry fit</TableCell>
                          <TableCell>{getRatingLabel(applicants?.candidate_score?.industry)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Salary Match</TableCell>
                          <TableCell>{getRatingLabel(applicants?.candidate_score?.salaryMatchRating)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ padding: '27px' }}></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <h3>Recruiter Feedback</h3>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>Job stability</TableCell>
                          <TableCell>{scores?.job_stability_score ? scoreLabels[scores.job_stability_score] : 'NA'} </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Career Progression</TableCell>
                          <TableCell>{scores?.career_progression_score ? scoreLabels[scores.career_progression_score] : 'NA'} </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Communication</TableCell>
                          <TableCell>{scores?.communication_score ? scoreLabels[scores.communication_score] : 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Technical & Finacial skills</TableCell>
                          <TableCell>{scores?.technical_subject_score ? scoreLabels[scores.technical_subject_score] : 'NA'} </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Behavioral skills</TableCell>
                          <TableCell>{scores?.behavioral_score ? scoreLabels[scores.behavioral_score] : 'NA'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* application status */}
            <Box sx={{ marginBottom: '1rem' }}>
              <Typography variant="h6" gutterBottom style={{ textAlign: 'left' }}>Recruiter: Enter Your Feedback</Typography>
            </Box>
            <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="status">Application Status</InputLabel>
                <Select labelId="status" id="status" value={status} onChange={handleStatus} label="Select an option">
                  <MenuItem disabled value="applied">Applied</MenuItem>
                  {/* <MenuItem disabled value="  ">Accepted</MenuItem> */}
                  <MenuItem value="shortlisted for interview">Shortlisted for interview</MenuItem>
                  <MenuItem value="final selection">Final selection</MenuItem>
                  <MenuItem value="offer made">Offer made</MenuItem>
                  <MenuItem value="offer accepted">Offer accepted</MenuItem>
                  <MenuItem value="joined firm">Joined firm</MenuItem>
                  <MenuItem value="offer declined">Offer declined</MenuItem>
                  <MenuItem value="not selected">Not selected</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* job stability */}
            <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Job stability</InputLabel>
                <Select labelId="score" value={scores?.job_stability_score || "NA"} onChange={(event) => handleScore(event, "job_stability_score")} label="Select Job stability">
                  <MenuItem value="NA">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* career progression */}
            <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Career progression</InputLabel>
                <Select labelId="score" value={scores?.career_progression_score || "NA"} onChange={(event) => handleScore(event, "career_progression_score")} label="Select Career progression">
                  {/* <MenuItem value={1}><em>Low experience quality & designation, low salary, Extended gaps, etc.</em></MenuItem>
                  <MenuItem value={4}>Experience quality high, steady designation progress, salary at par or above peer group</MenuItem> */}
                  <MenuItem value="NA">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* communication skills */}
            <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Communication skills</InputLabel>
                <Select labelId="score" value={scores?.communication_score || "NA"} onChange={(event) => handleScore(event, "communication_score")} label="Select Communication skills">
                  {/* <MenuItem value={1}><em>Unable to speak clearly/ coherently</em></MenuItem>
                  <MenuItem value={4}>Ability to clearly explain difficult concepts</MenuItem> */}
                  <MenuItem value="NA">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* technical subject knowledge */}
            <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Technical subject knowledge</InputLabel>
                <Select labelId="score" value={scores?.technical_subject_score || "NA"} onChange={(event) => handleScore(event, "technical_subject_score")} label="Select Technical subject knowledge">
                  <MenuItem value="NA">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Problem solving & detail orientation</InputLabel>
                <Select labelId="score" value={scores?.problem_solving_score || "NA"} onChange={(event) => handleScore(event, "problem_solving_score")} label="Select Problem solving & detail orientation">
                  <MenuItem value="NA">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box> */}
            {/* behavioral skills */}
            <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Behavioural skills (sincerity, honesty, learnability, teamwork)</InputLabel>
                <Select labelId="score" value={scores?.behavioral_score || "NA"} onChange={(event) => handleScore(event, "behavioral_score")} label="Select Behavioural skills (sincerity, honesty, learnability, teamwork)">
                  {/* <MenuItem value={1}><em>Poor behavioral skills</em></MenuItem>
                  <MenuItem value={4}>Attributes: Learnability, teamwork, leadership, humility/ courteousness</MenuItem> */}
                  <MenuItem value="NA">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Pre-assessment score</InputLabel>
                <Select labelId="score" value={scores.pre_assessment_score || ''} onChange={(event) => handleScore(event, "pre_assessment_score")} label="Select Pre-assessment score"> */}
            {/* <MenuItem value={1}><em>Below Average</em></MenuItem>
                  <MenuItem value={2}>Below Average</MenuItem>
                  <MenuItem value={3}>Above Average</MenuItem>
                  <MenuItem value={4}>Excellent</MenuItem>*/}
            {/* <MenuItem disabled value="">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Questionnaire score</InputLabel>
                <Select labelId="score" value={scores.questionnaire_score} onChange={(event) => handleScore(event, "questionnaire_score")} label="Select Questionnaire score"> */}
            {/* <MenuItem value={4}><em>Above 80% accurate</em></MenuItem>
                  <MenuItem value={3}>65-80% accurate</MenuItem>
                  <MenuItem value={2}>50-65% accurate</MenuItem>
                  <MenuItem value={1}>Below 50% accurate</MenuItem> */}
            {/* <MenuItem disabled value="">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ marginBottom: '1rem' }}>
              <FormControl fullWidth>
                <InputLabel id="score">Finance understanding</InputLabel>
                <Select labelId="score" value={scores.finance_understanding_score} onChange={(event) => handleScore(event, "finance_understanding_score")} label="Select Finance understanding"> */}
            {/* <MenuItem value={1}><em>Low level of finance knowledge</em></MenuItem>
                  <MenuItem value={4}>General finance knowledge</MenuItem> */}
            {/* <MenuItem disabled value="">NA</MenuItem>
                  <MenuItem value={3}>Good</MenuItem>
                  <MenuItem value={2}>Average</MenuItem>
                  <MenuItem value={1}>Poor</MenuItem>
                </Select>
              </FormControl>
            </Box> */}

            {/* For Candidate */}
            {/* <Box sx={{ marginBottom: '1rem' }}>
              <Typography variant="h6" style={{ textAlign: 'left' }}> For Candidate </Typography>
              <TextField label="Feedback" value={candidateFeedback} onChange={handleCandidateFeedback} fullWidth margin="normal" />
              <FormControlLabel control={<Checkbox checked={visibleToCandidate} onChange={handleCandidate} />} label="Candidate" />
              {(user.user_type === 'admin' || user.user_type === 'superadmin') && (
                <FormControlLabel control={<Checkbox checked={visibleToCompany} onChange={handleCompany} />} label="Company" />
              )}
              <Button variant="contained" color="primary" onClick={handleSubmitCandidateFeedback}> Add </Button>
            </Box> */}

            {/* related to application */}
            <Box sx={{ marginBottom: '1rem' }}>
              <Typography variant="h6" style={{ textAlign: 'left' }}>Enter comments based on your discussion with candidate</Typography>
              <TextField label="Feedback" value={applicationFeedback} onChange={handleApplicationFeedback} fullWidth margin="normal" />
              <FormControlLabel control={<Checkbox checked={visibleToCandidateApplication} onChange={handleCandidateApplication} />} label="Candidate" />
              {(user.user_type === 'admin' || user.user_type === 'superadmin') && (
                <FormControlLabel control={<Checkbox checked={visibleToCompanyApplication} onChange={handleCompanyApplication} />} label="Application" />
              )}
              <Button variant="contained" color="primary" disabled={!applicationFeedback} onClick={handleSubmitApplicationFeedback}> Add </Button>
            </Box>

            {/* feedback list */}
            <Paper elevation={3} sx={{ marginBottom: '1rem' }}>
              <div style={{ height: 'auto', width: '100%' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>SN</TableCell>
                        <TableCell>Feedback</TableCell>
                        <TableCell>For</TableCell>
                        <TableCell>Delete</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {feedbacks?.length === 0 ? (
                        <TableRow><TableCell colSpan={8}>No comment added yet.</TableCell></TableRow>) : (
                        feedbacks.map((feedback, index) => (
                          <TableRow key={feedback._id || index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{feedback.feedback}</TableCell>
                            <TableCell>
                              {[
                                feedback?.visible_to_candidate && "Candidate",
                                feedback?.visible_to_company && "Application",
                              ]
                                .filter(Boolean)
                                .join(" & ") || "-"}
                            </TableCell>
                            <TableCell> {feedback.is_deletable ? (
                              <Button onClick={() => handleDeleteFeedback(feedback._id)} variant="contained" color="secondary">Delete</Button>
                            ) : (<Button disabled>Delete</Button>)} </TableCell>
                          </TableRow>
                        )))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Paper>

            {/* candidate profile details */}
            {/* <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" gutterBottom>Profile</Typography>
                  {applicants?.resume?.length > 0 && <Button variant="contained" size="medium" onClick={handleNavigation}>Resume</Button>}
                </Stack>
                {applicants?.requirements_matched?.work_history.length === 0 || applicants?.requirements_matched?.education.length === 0 ? (
                  <Paper elevation={3} style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {applicants?.resume?.length > 0 && <Typography color="h6" sx={{ fontSize: '1rem', fontWeight: 'normal', marginBottom: '1rem', marginTop: '1rem', marginLeft: '1rem', }}>
                      Click on Resume Button</Typography>}
                  </Paper>
                ) : (
                  <Paper elevation={3} style={{ maxHeight: '300px', overflowY: 'auto' }}>


                  </Paper>
                )}
              </CardContent>
            </Card> */}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ApplicantProfile;
